import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react"
import { Input as UiInput } from "@/components/ui/input"
import { Label as UiLabel } from "@/components/ui/label"
import { cn } from "@/lib/utils"

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  label?: ReactNode
  error?: ReactNode
  helperText?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, helperText, className, id, ...rest },
  ref,
) {
  const reactId = useId()
  const inputId = id ?? `compass-input-${reactId}`
  const helperId = helperText ? `${inputId}-helper` : undefined
  const errorId = error ? `${inputId}-error` : undefined
  const describedBy = [errorId, helperId].filter(Boolean).join(" ") || undefined

  return (
    <div className="flex w-full flex-col gap-1">
      {label ? (
        <UiLabel htmlFor={inputId} className="text-markv-text-700">
          {label}
        </UiLabel>
      ) : null}
      <UiInput
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(error ? "border-markv-danger-500 aria-invalid:border-markv-danger-500" : "", className)}
        {...rest}
      />
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-markv-danger-accent">
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} className="text-xs text-markv-text-400">
          {helperText}
        </p>
      ) : null}
    </div>
  )
})
